import { cloneElement, ElementType, FC, MouseEventHandler, ReactElement, ReactNode } from 'react'
import { Dropdown } from './Dropdown'
import { Link } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/solid'
import { cn } from 'utilities/StringUtils'

interface Avatar {
  url?: string
  initials: string
}

interface Image {
  url?: string
  alt?: string
}

export interface ResourceListItemProps {
  avatar?: Avatar
  badge?: ReactNode
  classes?: Partial<{
    item: string
  }>
  className?: string
  compact?: boolean
  disabled?: boolean
  dropdownAboveModal?: boolean
  dropdownItems?: ReactElement[]
  offeringImage?: Image
  onClick?: MouseEventHandler<HTMLButtonElement>
  showDisclosureArrow?: boolean
  subtitle?: ReactNode
  title?: ReactNode
  titleLink?: string
  toggle?: ReactNode
  truncateSubtitle?: boolean
  truncateTitle?: boolean
}

export const ResourceListItem: FC<ResourceListItemProps> = ({
  toggle,
  title,
  titleLink,
  subtitle,
  truncateSubtitle = false,
  truncateTitle = false,
  badge,
  dropdownItems: inputDropdownItems = [],
  showDisclosureArrow = false,
  compact = false,
  disabled = false,
  avatar,
  offeringImage,
  dropdownAboveModal = false,
  onClick,
  classes,
  className,
}) => {
  const dropdownItems = inputDropdownItems.filter(Boolean).map((item, i) =>
    cloneElement(item, {
      key: `dropdown_item_${i}`,
    })
  )

  const interactive = Boolean(onClick && !disabled)
  const Item: ElementType = interactive ? 'button' : 'div'

  return (
    <li
      className={cn(
        'flex items-start overflow-hidden',
        {
          'hover:bg-gray-50 cursor-pointer group': showDisclosureArrow && !disabled,
          '!cursor-not-allowed opacity-50': disabled,
        },
        compact ? '[&>*]:py-3' : '[&>*]:py-4',
        className
      )}
    >
      <Item
        aria-label={typeof title === 'string' ? title : undefined}
        className={cn('px-4 flex flex-grow text-sm text-left', classes?.item)}
        disabled={interactive ? disabled : undefined}
        onClick={interactive ? (onClick as MouseEventHandler) : undefined}
        type={interactive ? 'button' : undefined}
      >
        <div className="flex items-center space-x-2">
          <div className="flex flex-1 items-center space-x-2">
            {avatar && (
              <div className="mr-2">
                {avatar?.url ? (
                  <img className="h-10 w-10 rounded-full" src={avatar.url} alt="" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="font-medium text-sm text-gray-700">{avatar.initials}</span>
                  </div>
                )}
              </div>
            )}

            {offeringImage && (
              <div className="mr-2 w-[100px] flex-shrink-0">
                <div className="bg-gray-300 dark:bg-gray-800 rounded-md overflow-hidden aspect-offering flex items-center relative">
                  {offeringImage.url && (
                    <img
                      src={offeringImage.url}
                      alt={offeringImage.alt}
                      className="w-full h-full aspect-offering object-center object-cover"
                    />
                  )}
                </div>
              </div>
            )}

            <div className="flex-1">
              <div className={`flex items-center space-x-1 ${truncateTitle && 'truncate'}`}>
                {toggle}
                {title && (
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {titleLink && !disabled && (
                      <Link to={titleLink} title={title.toString()} rel="noreferrer">
                        {title}
                      </Link>
                    )}
                    {(!titleLink || (titleLink && disabled)) && title}
                  </div>
                )}
                {badge}
              </div>

              {subtitle && (
                <div
                  className={`text-gray-500 ${compact ? 'mt-1' : 'mt-2'} ${
                    truncateSubtitle ? 'truncate' : ''
                  }`}
                >
                  {subtitle}
                </div>
              )}
            </div>
          </div>
          {showDisclosureArrow && dropdownItems.length === 0 && (
            <div className="flex-shrink-0 self-center pointer-events-none">
              <ChevronRightIcon
                className="h-6 w-6 group-hover:text-gray-600 text-gray-700"
                aria-hidden={true}
              />
            </div>
          )}
        </div>
      </Item>

      {dropdownItems.length > 0 && (
        <Dropdown threeDots aboveModal={dropdownAboveModal}>
          {dropdownItems}
        </Dropdown>
      )}
    </li>
  )
}
